import type {
  RestrictivePractice,
  RestrictivePractices
} from '@lmc/validations'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import IncidentCaseV2StepForm, { StepListItem } from '../IncidentCaseV2StepForm'
import type {
  IncidentCaseV2StepProps,
  IncidentCaseV2StepValues
} from '../incidentCaseV2Steps'
import { StepYesNoField } from '../StepYesNo'
import { StepSection } from '../stepLayout'
import { toAnswered } from '../stepValues'
import {
  type AppRestraintAnswers,
  restrictivePracticesFromApp
} from './restrictivePractices/appRestraints'
import { rebasePractice } from './restrictivePractices/practiceTimes'
import RestrictivePracticeList from './restrictivePractices/RestrictivePracticeList'

const testId = 'incident-case-v2-restrictive-practices'

type RestrictivePracticesValues = {
  details?: string
  entries: Array<RestrictivePractice>
  hasBeenApplied?: boolean
}

/** What the read returns that ts-common's report type lacks. */
type CaseFields = {
  case?:
    | ({ restrictivePractices?: RestrictivePractices } & AppRestraintAnswers)
    | null
  incidentOccurredAt?: string | null
}

/**
 * Step 4: whether restrictive practices were used and, if so, each one, added
 * in a drawer. Practice times fall after the incident's start from step 1.
 */
const RestrictivePracticesStep = ({
  incidentReport,
  handleRef,
  onDirtyChange
}: IncidentCaseV2StepProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { case: savedCase, incidentOccurredAt } =
    incidentReport as typeof incidentReport & CaseFields
  const incidentStart = dayjs(
    incidentOccurredAt ?? incidentReport.log.timeLogged
  )

  const initialValues = useMemo<RestrictivePracticesValues>(() => {
    const saved =
      savedCase?.restrictivePractices ?? restrictivePracticesFromApp(savedCase)
    return {
      details: saved?.details,
      entries: saved?.entries ?? [],
      hasBeenApplied: saved?.hasBeenApplied
    }
  }, [savedCase])

  const toStepValues = ({
    details,
    entries,
    hasBeenApplied
  }: RestrictivePracticesValues): IncidentCaseV2StepValues => {
    const restrictivePractices = toAnswered(hasBeenApplied, details, {
      entries: entries.map((entry) => rebasePractice(entry, incidentStart))
    })
    return restrictivePractices ? { restrictivePractices } : {}
  }

  return (
    <IncidentCaseV2StepForm
      handleRef={handleRef}
      onDirtyChange={onDirtyChange}
      initialValues={initialValues}
      toStepValues={toStepValues}
      testId={testId}
    >
      <StepSection>
        <StepYesNoField
          confirmNo={{
            body: t(
              incidentReportsConstants.CASE_V2_RESTRICTIVE_NO_CONFIRM_BODY
            ),
            isNeeded: (form) => form.getFieldValue('entries')?.length > 0,
            onConfirm: (form) => form.setFieldValue('entries', []),
            title: t(
              incidentReportsConstants.CASE_V2_RESTRICTIVE_NO_CONFIRM_TITLE
            )
          }}
          detailsLabel={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_DETAILS)}
          detailsName="details"
          detailsTestId={`${testId}-details`}
          label={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_ANY_USED)}
          name="hasBeenApplied"
          testId={`${testId}-any-used`}
        >
          <StepListItem
            name="entries"
            rules={[
              {
                validator: (_, entries: Array<RestrictivePractice>) =>
                  entries?.length
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          t(
                            incidentReportsConstants.CASE_V2_RESTRICTIVE_PRACTICE_REQUIRED
                          )
                        )
                      )
              }
            ]}
          >
            <RestrictivePracticeList
              incidentStart={incidentStart}
              testId={`${testId}-practices`}
            />
          </StepListItem>
        </StepYesNoField>
      </StepSection>
    </IncidentCaseV2StepForm>
  )
}

export default RestrictivePracticesStep
